import React, {useEffect, useState} from 'react';
import {Button, ListGroup, ListGroupItem} from 'reactstrap';
import {useDispatch, useSelector} from 'react-redux';
import {NavLink, useOutletContext} from 'react-router-dom';
import {formatCredits} from '../../../utils/credits';
import {imgUrl} from '../../../utils/api-request';
import {fetchMovieVersions} from '../../../slices/movie-versions.slice';
import SearchBar from '../../../components/SearchBar';
import DeleteButton from '../../../components/DeleteButton';
import SettingsPanel from './SettingsPanel';
import MediablockPanel from './MediablockPanel';
import {CinemaSummary} from './CinemaTab';
import {MembershipSummary} from './MembershipTab';

const OverviewTab = () => {
    const dispatch = useDispatch();
    const { organization, user, bacView, handleUpdate, handleDelete } = useOutletContext();
    const token = useSelector(state => state.auth.token);
    const { list: versionList } = useSelector(state => state.movieVersions);
    const [searchValue, setSearchInput] = useState('');
    const [filteredValues, setFilteredValues] = useState(versionList);
    const [highlightOpen, setHighlightOpen] = useState(false);

    const handleFilter = event => {
        event.preventDefault();
        setSearchInput(event.target.value);
        const searchTerm = event.target.value.toLowerCase();
        setFilteredValues(versionList.filter(({ movie, name }) => {
            return name.toLowerCase().includes(searchTerm) ||
                movie.title.toLowerCase().includes(searchTerm);
        }));
    };

    const saveHighlight = value => {
        handleUpdate('highlightMovieVersion')(value).then(() => setHighlightOpen(false));
    };

    useEffect(() => {
        if (!bacView) return;
        dispatch(fetchMovieVersions({ token }));
    }, [token, dispatch, bacView]);

    useEffect(() => {
        setFilteredValues(versionList);
    }, [versionList]);

    return (
        <div>
            <NavLink
                to={['site-user', 'site-guest'].includes(user.role) ? undefined : `/organizations/${bacView ? organization._id : 'own'}/transactions`}
                className='text-white text-decoration-none'
            >
                <div
                    className="d-flex position-relative border-start border-primary bg-broken details-card align-items-center mb-3">
                    <span
                        className="fas fa-coins bg-broken position-absolute align-items-center d-flex justify-content-center text-primary"/>
                    <div>
                        <p className="mb-0 text-uppercase text-primary">
                            Credits
                            <span className="fas fa-list ms-2 edit-icon"/>
                        </p>
                        <p className="mb-0">{formatCredits(organization.creditAmount)} credits</p>
                    </div>
                </div>
            </NavLink>

            {bacView && (
                <SettingsPanel
                    icon="fa-clapperboard"
                    title="Highlight Movie"
                    open={highlightOpen}
                    onToggle={setHighlightOpen}
                    summary={organization.highlightMovieVersion ? (
                        <div className="d-flex gap-3 align-items-center">
                            <img
                                className="logo-img"
                                width={75}
                                alt={organization.highlightMovieVersion.movie.title}
                                src={imgUrl(organization.highlightMovieVersion.movie.poster.imageUrl)}
                            />

                            <div className="title text-uppercase text-primary d-flex flex-column flex-lg-row">
                                {organization.highlightMovieVersion.movie.title} <br/>
                                {organization.highlightMovieVersion.name}
                            </div>
                        </div>
                    ) : (
                        <div className="text-danger">
                            Select highlight movie version
                        </div>
                    )}
                >
                    <SearchBar onChange={handleFilter} initialValue={searchValue} />

                    <ListGroup className="highlight-picker mt-2">
                        {filteredValues.map(version => (
                            <ListGroupItem
                                key={version._id}
                                onClick={() => saveHighlight(version._id)}
                                style={{ borderRadius: 0 }}
                                className="d-flex justify-content-start align-items-center gap-3 border-start
                            border-primary border-2 bg-broken text-white border-0 pointer mb-2"
                            >
                                <img src={imgUrl(version.movie.poster?.imageUrl)} width={50} alt={version.movie?.title} />
                                <div className="flex-grow-1">
                                    {version.movie?.title}<br />
                                    <small>{version.name}</small>
                                </div>
                            </ListGroupItem>
                        ))}
                    </ListGroup>

                    <div className="d-flex justify-content-end mt-2">
                        <Button color="danger" onClick={() => saveHighlight(null)}>
                            <span className="fas fa-times me-2"/>
                            Clear Highlight
                        </Button>
                    </div>
                </SettingsPanel>
            )}

            {/* Site users have no dedicated Cinema/Systems/Membership tabs;
                their read-only details live here instead. */}
            {!bacView && (
                <>
                    <SettingsPanel
                        icon="fa-video"
                        title="Cinema Properties"
                        summary={<CinemaSummary cinemaSettings={organization?.cinemaSettings} />}
                    />

                    <MediablockPanel organization={organization} bacView={bacView} handleUpdate={handleUpdate} />

                    <SettingsPanel
                        icon="fa-book-open"
                        title="Membership"
                        summary={<MembershipSummary membership={organization.membership} />}
                    />
                </>
            )}

            {bacView && (
                <div className="d-flex justify-content-end mb-3">
                    <DeleteButton
                        entityId={organization._id}
                        entityLabel="organization"
                        title={organization.name}
                        onClick={handleDelete}
                        typeToConfirm
                    />
                </div>
            )}
        </div>
    );
};

export default OverviewTab;
